import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Lock, Search, X } from 'lucide-react';
import { suggestValues } from '../../Services/DataService';

interface Props {
  connectionId: string;
  tableName: string;
  columnName: string;
  value: string;
  onChange: (value: string) => void;
  /** False when the column is on the PII blocklist or has no suggestions. */
  allowed: boolean;
  placeholder?: string;
}

/**
 * Prefix-only value lookup. The server caps this at 20 rows with a two second
 * timeout, and refuses blocked columns outright, so the dropdown stays small.
 */
const SuggestedValueInput = ({
  connectionId,
  tableName,
  columnName,
  value,
  onChange,
  allowed,
  placeholder,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  const ready = Boolean(connectionId && tableName && columnName && allowed);

  const fetchSuggestions = useCallback(
    async (prefix: string) => {
      if (!ready) return;
      setLoading(true);
      setNote('');
      try {
        const response = await suggestValues({
          connectionId,
          tableName,
          columnName,
          prefix: prefix || undefined,
          limit: 20,
        });
        if (response.success && response.data) {
          setOptions(response.data.values ?? []);
          if (response.data.capped) {
            setNote('Showing the first 20 matches. Keep typing to narrow it down.');
          }
        } else {
          setOptions([]);
          setNote(response.message || 'No suggestions available for this column.');
        }
      } catch (err) {
        console.error('Failed to load value suggestions', err);
        setOptions([]);
        setNote('Suggestions are unavailable right now.');
      } finally {
        setLoading(false);
      }
    },
    [ready, connectionId, tableName, columnName]
  );

  // Debounced so a fast typist does not fire a request per keystroke.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => fetchSuggestions(value.trim()), 300);
    return () => clearTimeout(timer);
  }, [open, value, fetchSuggestions]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!allowed) {
    return (
      <div className="relative">
        <Lock size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={value}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
        />
        {loading ? (
          <Loader2
            size={14}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-cyan-500"
          />
        ) : (
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-600"
          />
        )}
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 shadow-2xl">
          {loading && options.length === 0 && (
            <p className="px-3.5 py-3 text-xs font-bold text-slate-500">Loading values…</p>
          )}

          {!loading && options.length === 0 && (
            <p className="px-3.5 py-3 text-xs font-bold text-slate-500">
              {note || 'No matching values.'}
            </p>
          )}

          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              className="block w-full truncate px-3.5 py-2 text-left text-xs font-semibold text-slate-300 transition hover:bg-cyan-500/10 hover:text-cyan-300"
            >
              {option}
            </button>
          ))}

          {note && options.length > 0 && (
            <p className="border-t border-slate-800 px-3.5 py-2 text-[10px] font-bold text-slate-500">
              {note}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default SuggestedValueInput;
